// PWA Install Tracking Utility

// Detect device type
const getDeviceType = () => {
  const ua = navigator.userAgent;
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
    return 'tablet';
  }
  if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
    return 'mobile';
  }
  return 'desktop';
};

// Detect OS
const getOS = () => {
  const ua = navigator.userAgent;
  if (/android/i.test(ua)) return 'Android';
  if (/iPad|iPhone|iPod/.test(ua)) return 'iOS';
  if (/Win/i.test(ua)) return 'Windows';
  if (/Mac/i.test(ua)) return 'MacOS';
  if (/Linux/i.test(ua)) return 'Linux';
  return 'Unknown';
};

// Detect browser
const getBrowser = () => {
  const ua = navigator.userAgent;
  if (/chrome|crios|crmo/i.test(ua)) return 'Chrome';
  if (/firefox|fxios/i.test(ua)) return 'Firefox';
  if (/safari/i.test(ua) && !/chrome|crios|crmo/i.test(ua)) return 'Safari';
  if (/edg/i.test(ua)) return 'Edge';
  if (/opr\//i.test(ua)) return 'Opera';
  return 'Unknown';
};

// Get screen dimensions
const getScreenDimensions = () => {
  return {
    width: window.screen.width,
    height: window.screen.height
  };
};

// Get timezone
const getTimezone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

// Log PWA install to backend
export const logPWAInstall = async (userId = null) => {
  try {
    const screen = getScreenDimensions();
    
    const installData = {
      device_type: getDeviceType(),
      os: getOS(),
      browser: getBrowser(),
      source_page: window.location.pathname,
      user_agent: navigator.userAgent,
      screen_width: screen.width,
      screen_height: screen.height,
      timezone: getTimezone(),
      user_id: userId
    };

    const response = await fetch(`${window.location.origin}/api/pwa/install`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(installData)
    });

    const data = await response.json();
    
    if (data.success) {
      console.log('✅ PWA install logged successfully');
      // Note: Session tracking is handled in initPWATracking, not here
      // This allows multiple users on different devices to be tracked
    }
  } catch (error) {
    console.error('Error logging PWA install:', error);
  }
};

// Initialize PWA tracking
export const initPWATracking = () => {
  // Check if THIS specific install event was already tracked
  // Use a session-based flag instead of permanent localStorage
  const sessionTracked = sessionStorage.getItem('pwa_install_logged_this_session');
  if (sessionTracked === 'true') {
    console.log('PWA install already tracked this session');
    return;
  }

  let deferredPrompt;

  // Listen for beforeinstallprompt event
  window.addEventListener('beforeinstallprompt', (e) => {
    console.log('PWA install prompt available');
    // Prevent the mini-infobar from appearing on mobile
    e.preventDefault();
    // Stash the event so it can be triggered later
    deferredPrompt = e;
    
    // Update UI to show install button (you can add this later)
    // showInstallPromotion();
  });

  // Listen for appinstalled event
  window.addEventListener('appinstalled', (e) => {
    console.log('PWA was installed');
    deferredPrompt = null;
    
    // Check if already tracked in this session
    if (sessionStorage.getItem('pwa_install_logged_this_session') === 'true') {
      console.log('Already tracked in this session, skipping duplicate');
      return;
    }
    
    // Get user ID if logged in
    const userStr = localStorage.getItem('user');
    let userId = null;
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        userId = user.id;
      } catch (e) {
        console.error('Error parsing user:', e);
      }
    }
    
    // Log the install
    logPWAInstall(userId);
    
    // Mark as tracked for THIS session only (not permanent)
    sessionStorage.setItem('pwa_install_logged_this_session', 'true');
  });

  // Detect if already running as PWA
  if (window.matchMedia('(display-mode: standalone)').matches) {
    console.log('Running as PWA');
    // Check if we've already logged this install
    if (localStorage.getItem('pwa_install_logged') !== 'true') {
      // Get user ID if logged in
      const userStr = localStorage.getItem('user');
      let userId = null;
      if (userStr) {
        try {
          const user = JSON.parse(userStr);
          userId = user.id;
        } catch (e) {
          console.error('Error parsing user:', e);
        }
      }
      
      // Log the install
      logPWAInstall(userId);
      
      // IMPORTANT: Mark as logged to prevent duplicate counting
      localStorage.setItem('pwa_install_logged', 'true');
    }
  }
};

// Register service worker with update handling
export const registerServiceWorker = () => {
  if ('serviceWorker' in navigator) {
    window.addEventListener('load', () => {
      navigator.serviceWorker
        .register('/service-worker.js')
        .then((registration) => {
          console.log('✅ Service Worker registered:', registration);
          
          // Check for updates every hour
          setInterval(() => {
            registration.update();
          }, 60 * 60 * 1000);
          
          // Handle updates
          registration.addEventListener('updatefound', () => {
            const newWorker = registration.installing;
            console.log('🔄 New Service Worker version found!');
            
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                console.log('✨ New version available! App will update on next visit.');
                
                // Optionally show a toast notification
                if (window.toast && typeof window.toast.info === 'function') {
                  window.toast.info('New version available! Close and reopen the app to update.');
                }
                
                // Auto-reload after 3 seconds (optional - can be removed if you prefer manual reload)
                setTimeout(() => {
                  newWorker.postMessage({ type: 'SKIP_WAITING' });
                  window.location.reload();
                }, 3000);
              }
            });
          });
        })
        .catch((error) => {
          console.error('❌ Service Worker registration failed:', error);
        });
      
      // Handle controller change (when new service worker takes over)
      let refreshing = false;
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (!refreshing) {
          refreshing = true;
          console.log('🔄 Service Worker updated, reloading page...');
          window.location.reload();
        }
      });
    });
  }
};
